import type { FunctionComponent } from 'react';
import { useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { paletteHelper, spacingHelper, radiusHelper, boxShadow } from '@property.com.au/ui-kit';
import { PageContainer } from '../../../../../app-kit/components/PageContainer/PageContainer';
import { mapPinToMarkerFactory } from '../../../../../app-kit/components/PcaMap/helpers/mapPinToMarkerFactory';
import { GeoJsonData, PcaMap } from '../../../../../app-kit/components/PcaMap/PcaMap';
import { FullScreenButton } from '../../../../../app-kit/components/PcaMap/subcomponent/FullScreenButton/FullScreenButton';
import { ZoomButtons } from '../../../../../app-kit/components/PcaMap/subcomponent/ZoomButtons/ZoomButtons';
import { PcaMapContextProvider } from '../../../../../contexts/PcaMapContext/PcaMapContextProvider';
import { onSchoolCatchmentMapLoaded } from './helpers/onSchoolCatchmentMapLoaded';
import { MapInteractionEventData } from '../../../../../analytics/event-tracking/trackMapInteraction';

export interface MapInteractionTracking {
  changeZoomLevel: MapInteractionEventData;
  toggleFullscreen: MapInteractionEventData;
}

const SchoolCatchmentMapContainer = styled(PageContainer)`
  position: relative;
  height: 496px;
  ${boxShadow.defaultBoxShadow};
  background: ${paletteHelper('backgroundPrimaryHigh')};
  border-radius: ${radiusHelper('large')};
  .gm-style {
    .control-buttons {
      display: block;
    }
  }
`;
const ControlButtonsContainer = styled.div`
  display: none;
  transition: 0.2s;
  margin-top: ${spacingHelper('small')};
  margin-right: ${spacingHelper('small')};
  > button {
    margin-top: ${spacingHelper('small')};
  }
`;

interface SchoolCatchmentMapBrickProps {
  schoolPin?: {
    latitude: number;
    longitude: number;
  };
  catchmentZones?: GeoJsonData | null;
  tracking?: MapInteractionTracking | null;
}

export const SchoolCatchmentMapBrick: FunctionComponent<SchoolCatchmentMapBrickProps> = ({
  schoolPin,
  catchmentZones,
  tracking,
}) => {
  const controlButtonsRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  if (!schoolPin) return null;
  const { latitude: lat, longitude: lng } = schoolPin;

  return (
    <SchoolCatchmentMapContainer data-testid="school-catchment-map-container">
      <PcaMapContextProvider
        center={{ lat, lng }}
        markerFactories={[mapPinToMarkerFactory(schoolPin)]}
        onMapLoaded={onSchoolCatchmentMapLoaded({
          catchmentZones,
          tracking,
          controlButtonsRef,
          theme,
        })}
        outboundLinkType="google-map-link-to-school-location"
        zoomControl={false}
        fullscreenControl={false}
      >
        <PcaMap data-testid="school-catchment-map" />
        <ControlButtonsContainer ref={controlButtonsRef} className="control-buttons">
          <ZoomButtons />
          <FullScreenButton />
        </ControlButtonsContainer>
      </PcaMapContextProvider>
    </SchoolCatchmentMapContainer>
  );
};
